import type { FetchResult } from "@apollo/client";
import { createAsyncThunk } from "@reduxjs/toolkit";

import type {
  DetailedIntermodalBooking,
  UpdateIntermodalBookingResult,
} from "../../../common/graphql/fragments/gql/IntermodalBooking";
import type { Message } from "../../../common/update-information/utils/messagesResult";
import type { UpdateIntermodalBookingInput } from "../../../gql/graphql";
import type { StoreState } from "../../../store/storeState";
import { transformIntermodalBookingToUpdateFormState } from "../../../use-cases/intermodal/utils/detailsUtils";
import {
  getUpdateIntermodalBookingEditedFormState,
  type UpdateIntermodalBookingFormModel,
  type UpdateIntermodalBookingValidationErrors,
} from "../updateIntermodalReducer";
import { validateUpdateIntermodalFields } from "../validation/validators";

type UpdateIntermodalBookingThunkRejectValue =
  | {
      type: "validationErrors";
      errors: UpdateIntermodalBookingValidationErrors;
    }
  | { type: "outOfDate" }
  | { type: "error"; errors: Message[] };

export const updateIntermodalBookingThunk = createAsyncThunk<
  {
    booking: DetailedIntermodalBooking;
    formState: UpdateIntermodalBookingFormModel;
    warnings: Message[];
    messages: Message[];
  },
  {
    updateBooking: (
      updateInput: UpdateIntermodalBookingInput,
    ) => Promise<FetchResult<UpdateIntermodalBookingResult>>;
    onOutdatedBooking: () => void;
  },
  { state: StoreState; rejectValue: UpdateIntermodalBookingThunkRejectValue }
>(
  "update-intermodal-booking",
  async (
    { updateBooking, onOutdatedBooking },
    { getState, rejectWithValue, fulfillWithValue },
  ) => {
    const validationResult = validateUpdateIntermodalFields(
      getUpdateIntermodalBookingEditedFormState(getState()),
    );

    if (!validationResult.success) {
      return rejectWithValue({
        type: "validationErrors",
        errors: validationResult.error,
      });
    }

    const updateInput = validationResult.value;

    const result = await updateBooking(updateInput);
    const updateResult = result.data?.updateIntermodalBooking;

    if (updateResult?.success && updateResult.booking) {
      return fulfillWithValue({
        booking: updateResult.booking,
        formState: transformIntermodalBookingToUpdateFormState(
          updateResult.booking,
        ),
        warnings: updateResult.warnings,
        messages: updateResult.messages,
      });
    } else if (updateResult?.bookingOutOfDate) {
      onOutdatedBooking();
      return rejectWithValue({
        type: "outOfDate",
      });
    } else {
      return rejectWithValue({
        type: "error",
        errors: updateResult?.errors ?? [],
      });
    }
  },
);
